import React, { useState, useEffect } from 'react';
import { storage } from '../firebase';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { variantURL, fallbackToOriginal } from '../utils/imageVariant';
import PlacesAPI from '../api/PlacesAPI';

const GlobalPhotosLibraryModal = ({ show, onClose, onConfirm, existingPhotos = [], placeId, venueName, venueAddress }) => {
  const [activeTab, setActiveTab] = useState('places');
  const [images, setImages] = useState([]);
  const [placePhotos, setPlacePhotos] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlacePhotos, setLoadingPlacePhotos] = useState(false);

  // Reset to Google Places tab whenever modal opens
  useEffect(() => {
    if (show) {
      setActiveTab('places');
      setSelectedImages([]);
      setPlacePhotos([]);
      setLoadingPlacePhotos(true); // show spinner immediately before fetch kicks off
    }
  }, [show]);

  // Fetch Firebase library photos
  useEffect(() => {
    if (show) {
      setLoading(true);
      const fetchImages = async () => {
        const storageRef = ref(storage, 'event');
        try {
          const eventFolders = await listAll(storageRef);
          const imageUrls = await Promise.all(
            eventFolders.prefixes.flatMap(async (folderRef) => {
              const photoRef = ref(storage, `${folderRef.fullPath}/eventPhotos`);
              const items = await listAll(photoRef);
              return Promise.all(
                items.items.map(async (itemRef) => {
                  const url = await getDownloadURL(itemRef);
                  const metadata = await getMetadata(itemRef);
                  return { url, name: itemRef.name, timeCreated: metadata.timeCreated };
                })
              );
            })
          );
          const sortedImages = imageUrls.flat().sort((a, b) => new Date(b.timeCreated) - new Date(a.timeCreated));
          const existingUrls = existingPhotos.filter(photo => typeof photo === 'string');
          const filteredImages = sortedImages.filter(image => !existingUrls.includes(image.url));
          setImages(filteredImages);
        } catch (error) {
          console.error("Error fetching images from Firebase Storage:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchImages();
    }
  }, [show]);

  // Fetch Google Places photos when the modal opens or the tab is switched to 'places'.
  // If no placeId is stored, fall back to a Text Search using venueName + venueAddress.
  useEffect(() => {
    if (show && activeTab === 'places') {
      setLoadingPlacePhotos(true);
      PlacesAPI.resolvePlaceId(placeId, venueName, venueAddress)
        .then((resolvedId) => PlacesAPI.getPhotoUris(resolvedId))
        .then((urls) => {
          setPlacePhotos(urls);
          setLoadingPlacePhotos(false);
        });
    }
  }, [show, activeTab]);

  const toggleSelection = (url) => {
    setSelectedImages((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedImages);
    onClose();
  };

  if (!show) return null;

  const currentImages = activeTab === 'library'
    ? images.map((img) => img.url)
    : placePhotos;

  const isLoading = activeTab === 'library' ? loading : loadingPlacePhotos;

  return (
    <div
      className="fixed !m-0 inset-0 bg-black bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="relative min-w-72 mx-auto p-2 border border-white/30 max-w-6xl overflow-y-auto shadow-lg rounded-md bg-white/20 backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mt-3 text-center">
          {/* Tab switcher */}
          <div className="flex gap-2 justify-center mb-3">
            <button
              type="button"
              onClick={() => (placeId || venueName) && setActiveTab('places')}
              title={!placeId && !venueName ? 'Enter a venue name first to load Google Places photos' : ''}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'places'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                  : (placeId || venueName)
                  ? 'bg-white/20 text-white hover:bg-white/30'
                  : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
            >
              Google Places
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('library')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'library'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Library
            </button>
          </div>

          {isLoading ? (
            <p className="text-white/70 p-8 animate-pulse">Loading...</p>
          ) : (
            <>
              <h3 className="text-lg leading-6 font-medium text-white">
                {activeTab === 'library' ? 'Select Photos' : 'Venue Photos from Google'}
              </h3>

              {activeTab === 'places' && placePhotos.length === 0 && !loadingPlacePhotos && (
                <p className="text-white/60 text-sm p-8">No photos found for this venue.</p>
              )}

              <div className="mt-2">
                <div className="grid grid-cols-2 md:grid-cols-4 h-[80dvh] sm:h-[70vh] overflow-y-auto">
                  {currentImages.map((url, index) => (
                    <div
                      key={index}
                      className={`cursor-pointer border-4 ${
                        selectedImages.includes(url) ? 'border-primary' : 'border-transparent'
                      }`}
                      onClick={() => toggleSelection(url)}
                    >
                      <img src={variantURL(url, 400)} onError={fallbackToOriginal(url)} alt={`photo-${index}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="items-center justify-center px-4 py-3 flex flex-row gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="px-4 py-2 bg-primary bg-opacity-85 backdrop-blur-md text-white border-2 border-white/30 text-base font-medium rounded-md w-full max-w-40 shadow-sm hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-base border-2 border-white/50 font-medium rounded-md w-full max-w-40 shadow-sm hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalPhotosLibraryModal;
